import { ExternalLink, Plus, Share2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { CreateShareDialog } from "@/components/document-sharing/CreateShareDialog";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  buildShareUrlFromToken,
  type ShareSummary,
  useProjectShares,
  useRevokeShare,
} from "@/hooks/use-document-shares";
import { useProjects } from "@/hooks/use-projects";

type StatusFilter = "active" | "revoked" | "expired";

// "all" is the sentinel value the dropdown uses for "no project filter".
const ALL_PROJECTS = "all" as const;

export function DocumentSharesPage() {
  const projectsQuery = useProjects();
  // Default to cross-project view.
  const [projectFilter, setProjectFilter] = useState<string>(ALL_PROJECTS);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [createOpen, setCreateOpen] = useState(false);
  // For the share dialog when no specific file is locked, we still need a
  // project to scope the file picker. Default to the first project.
  const projects = projectsQuery.data ?? [];
  const dialogProjectId = projectFilter !== ALL_PROJECTS ? projectFilter : projects[0]?.id;

  return (
    <div className="container mx-auto max-w-6xl py-6">
      <PageHeader
        title="Document Shares"
        description="Manage public share links and folder snapshots for project documents."
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={!dialogProjectId}>
            <Plus className="mr-2 h-4 w-4" />
            Create share
          </Button>
        }
      />

      <div className="my-4 flex items-center gap-3">
        <Label htmlFor="project-select" className="text-sm text-gray-600">
          Project
        </Label>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger id="project-select" className="w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PROJECTS}>All projects</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Tabs value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <TabsList>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="expired">Expired</TabsTrigger>
            <TabsTrigger value="revoked">Revoked</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <SharesTable projectFilter={projectFilter} status={status} />

      {dialogProjectId && (
        <CreateShareDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          projectId={dialogProjectId}
        />
      )}
    </div>
  );
}

function SharesTable({ projectFilter, status }: { projectFilter: string; status: StatusFilter }) {
  const sharesQuery = useProjectShares(projectFilter as string | "all", status);
  const revoke = useRevokeShare(projectFilter as string | "all");

  if (sharesQuery.isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }
  if (sharesQuery.isError) {
    return <ErrorState message="Failed to load shares" onRetry={() => sharesQuery.refetch()} />;
  }
  const shares = sharesQuery.data ?? [];
  if (shares.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-12 text-center text-sm text-gray-500">
        No {status} shares
        {projectFilter !== ALL_PROJECTS ? " for this project" : ""}.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Project</TableHead>
            <TableHead>Document</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Gate</TableHead>
            <TableHead>Accesses</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shares.map((s) => (
            <ShareRow
              key={s.id}
              share={s}
              onRevoke={(reason) => revoke.mutate({ shareId: s.id, reason })}
              revoking={revoke.isPending}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ShareRow({
  share,
  onRevoke,
  revoking,
}: {
  share: ShareSummary;
  onRevoke: (reason?: string) => void;
  revoking: boolean;
}) {
  const isActive = !share.revokedAt && new Date(share.expiresAt).getTime() > Date.now();
  const modeLabel =
    share.mode === "file"
      ? "File"
      : share.mode === "folder_snapshot"
        ? "Folder snapshot"
        : "Folder live mirror";
  const docLabel = share.fileName ?? share.folderPath ?? (share.folderId ? "Folder" : "—");
  const url = buildShareUrlFromToken(share.rawToken, share.mode);

  function copyLink() {
    if (!url) {
      toast.error("URL unavailable for this share. Create a new one.");
      return;
    }
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  }

  function openLink() {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function handleRevoke() {
    const reason = prompt("Optional reason for revoking:");
    onRevoke(reason ?? undefined);
  }

  return (
    <TableRow>
      <TableCell className="text-sm">{share.projectName ?? "—"}</TableCell>
      <TableCell className="max-w-xs truncate text-sm" title={docLabel}>
        {docLabel}
      </TableCell>
      <TableCell>
        <Badge variant="outline">{modeLabel}</Badge>
      </TableCell>
      <TableCell className="text-sm text-gray-600">
        {new Date(share.createdAt).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-sm text-gray-600">
        {new Date(share.expiresAt).toLocaleDateString()}
      </TableCell>
      <TableCell>
        {share.revokedAt ? (
          <Badge variant="destructive">Revoked</Badge>
        ) : isActive ? (
          <Badge>Active</Badge>
        ) : (
          <Badge variant="secondary">Expired</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-gray-600">
        {share.emailGateEnabled ? "Email" : "Open"}
        {share.hasPin ? " + PIN" : ""}
      </TableCell>
      <TableCell className="text-sm">{share.accessCount}</TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={copyLink} disabled={!url}>
          <Share2 className="mr-1 h-3.5 w-3.5" />
          Copy
        </Button>
        <Button variant="ghost" size="sm" onClick={openLink} disabled={!url}>
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          Open
        </Button>
        {isActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRevoke}
            disabled={revoking}
            className="text-red-600 hover:text-red-700"
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Revoke
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
